import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { useJsApiLoader } from '@react-google-maps/api';
import { Shield, Zap, Target, Settings, Car, Home } from 'lucide-react';
import './App.css';
import LocationView from './Map';
import LandingPage from './LandingPage';
import GoogleReviews from './GoogleReviews';

const markets = [
  { name: 'San Francisco', baseRate: 8.50, enforcementRate: 0.85, marketingMultiplier: 1.25 },
  { name: 'Austin', baseRate: 6.75, enforcementRate: 0.78, marketingMultiplier: 1.20 },
  { name: 'Baltimore', baseRate: 5.25, enforcementRate: 0.72, marketingMultiplier: 1.15 },
  { name: 'Charleston', baseRate: 4.50, enforcementRate: 0.68, marketingMultiplier: 1.12 },
  { name: 'Denver', baseRate: 7.00, enforcementRate: 0.80, marketingMultiplier: 1.22 },
  { name: 'Miami', baseRate: 6.25, enforcementRate: 0.75, marketingMultiplier: 1.18 },
  { name: 'Seattle', baseRate: 7.50, enforcementRate: 0.82, marketingMultiplier: 1.23 },
  { name: 'Nashville', baseRate: 5.75, enforcementRate: 0.70, marketingMultiplier: 1.16 }
];

const greenShades = ['#064e3b', '#047857', '#059669', '#10b981'];
const dpLabels = ['Disabled', 'Conservative', 'Recommended', 'Aggressive'];
const dpUplifts = [1.0, 1.08, 1.15, 1.25];
const marketingChannels = ['SpotHero', 'ParkWhiz', 'Google Maps Ads', 'SEO Optimized Page', 'Local Demand Gen'];
const enforcementOptions = ['Tickets', 'Auto-starts'];

const libraries = ['places', 'geometry'];

function App() {
  const [address, setAddress] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState(markets[0]);
  const [parkingSpaces] = useState(50);
  const [utilizationRate] = useState(75);
  const [dynamicPricingLevel, setDynamicPricingLevel] = useState(2);
  const [marketingEnabled, setMarketingEnabled] = useState(true);
  const [selectedMarketingChannels, setSelectedMarketingChannels] = useState(['SpotHero', 'ParkWhiz']);
  const [enforcementEnabled, setEnforcementEnabled] = useState(true);
  const [selectedEnforcementOptions, setSelectedEnforcementOptions] = useState(['Tickets', 'Auto-starts']);
  const [complianceRate, setComplianceRate] = useState(96);
  const [ticketPrice, setTicketPrice] = useState(50);
  const [ticketPaymentRate, setTicketPaymentRate] = useState(40);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyBjMoiDAimpIhmRmg5A8kXlsWoDdeCHSgY",
    libraries,
    version: 'beta',
  });

  const handleLocationSubmit = (newAddress) => {
    setAddress(newAddress);
  };

  const handleStartOver = () => {
    setAddress(null);
  };

  const calculatePnlData = () => {
    const baseRevenue = parkingSpaces * (utilizationRate / 100) * selectedMarket.baseRate * 365;
    
    // Traditional Operator
    const traditionalGross = baseRevenue * 0.85;
    const traditionalOpex = parkingSpaces * 1200;
    const traditionalOwnerIncome = traditionalGross - traditionalOpex;

    // AirGarage
    const costSavings = traditionalOpex;
    
    const rev_base = baseRevenue;
    const rev_dp = rev_base * dpUplifts[dynamicPricingLevel];
    const rev_marketing = marketingEnabled ? rev_dp * selectedMarket.marketingMultiplier : rev_dp;
    const rev_final = enforcementEnabled ? rev_marketing * (1 + selectedMarket.enforcementRate * 0.3) : rev_marketing;

    const airgarageOpex = rev_final * 0.15;

    const income_base = costSavings + (rev_base * 0.85) - (rev_base * 0.15);
    const income_dp_uplift = (rev_dp - rev_base) * 0.85;
    const income_marketing_uplift = (rev_marketing - rev_dp) * 0.85;
    const income_enforcement_uplift = (rev_final - rev_marketing) * 0.85;
    
    const airgarageOwnerIncome = income_base + income_dp_uplift + income_marketing_uplift + income_enforcement_uplift;

    return {
      pnlChartData: [
        {
          name: 'Prior Operator',
          'Prior Owner Income': traditionalOwnerIncome > 0 ? traditionalOwnerIncome : 0,
          'Prior OpEx': traditionalOpex,
        },
        {
          name: 'AirGarage',
          'Base Income': income_base,
          'Dynamic Pricing Uplift': income_dp_uplift,
          'Marketing Uplift': income_marketing_uplift,
          'Enforcement Uplift': income_enforcement_uplift,
          'AG OpEx': airgarageOpex,
        }
      ],
      uplift: ((airgarageOwnerIncome - traditionalOwnerIncome) / traditionalOwnerIncome) * 100,
      traditional: { net: traditionalOwnerIncome },
      airgarage: { net: airgarageOwnerIncome }
    };
  };

  const calculateCaptureData = () => {
    const parkingRevenue = complianceRate;
    const nonCompliance = 100 - complianceRate;
    // This formula models that ticket revenue is a function of non-compliance and ticket price.
    // The multipliers are set to yield ~8% ticket revenue at 96% compliance and $50 ticket price, as in the original static chart.
    const ticketRevenue = nonCompliance * 2 * (ticketPrice / 50) * (ticketPaymentRate / 40);

    return [
      {
        name: 'Prior Operator',
        'Parking Revenue': 100,
      },
      {
        name: 'AirGarage',
        'Parking Revenue': parkingRevenue,
        'Ticket Revenue': ticketRevenue,
      },
    ];
  }

  const { pnlChartData, uplift, traditional, airgarage } = calculatePnlData();
  const captureChartData = calculateCaptureData();

  if (!isLoaded) return <div>Loading...</div>;

  if (!address) {
    return <LandingPage onLocationSubmit={handleLocationSubmit} />;
  }

  return (
    <div className="App">
      <header className="header">
        <div className="container">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img src="https://cdn.prod.website-files.com/5d55f1425cb6b7a18aa77528/5ff7de92714ca4713ab426d7_AirGarage%20Full%20White%20SVG.webp" alt="AirGarage Logo" className="header-logo" />
              <div>
                <h1 className="text-2xl font-bold">Revenue Uplift Calculator</h1>
                <p>See how going gateless increases your parking revenue</p>
              </div>
            </div>
            <button onClick={handleStartOver} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700">
              <Home size={16} />
              Start Over
            </button>
          </div>
        </div>
      </header>

      <main className="container">
        {/* Location & Map Module */}
        <div className="card mb-8">
          <h2 className="text-xl font-semibold mb-4"><span className="font-normal text-gray-400">{address}</span></h2>
          <LocationView address={address} />
        </div>

        <div className="grid main-grid mb-8">
          {/* P&L CHART */}
          <div className="card">
            <div className="flex items-center justify-center gap-3 mb-6">
              <h2 className="text-xl font-semibold text-center">Prior Operator vs. AirGarage P&L</h2>
            </div>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={pnlChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(value) => `$${(value / 1000).toLocaleString()}k`} />
                <Tooltip formatter={(value, name) => [value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }), name]} />
                
                {/* Prior Operator Bars */}
                <Bar name="Owner Income" dataKey="Prior Owner Income" stackId="prior" fill={greenShades[2]} />
                <Bar name="OpEx" dataKey="Prior OpEx" stackId="prior" fill="#dc2626" />
                
                {/* AirGarage Bars */}
                <Bar name="Base Income" dataKey="Base Income" stackId="ag" fill={greenShades[0]} />
                <Bar name="Dynamic Pricing" dataKey="Dynamic Pricing Uplift" stackId="ag" fill={greenShades[1]} />
                <Bar name="Marketing" dataKey="Marketing Uplift" stackId="ag" fill={greenShades[2]} />
                <Bar name="Enforcement" dataKey="Enforcement Uplift" stackId="ag" fill={greenShades[3]} />
                <Bar name="OpEx" dataKey="AG OpEx" stackId="ag" fill="#dc2626" radius={[4, 4, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Input Panel */}
          <div className="card control-panel">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="text-xl font-semibold">Revenue Comparison</h2>
            </div>
            
            <div className="input-group">
              <label>Market Data</label>
              <select 
                value={selectedMarket.name} 
                onChange={(e) => setSelectedMarket(markets.find(m => m.name === e.target.value))}
              >
                {markets.map(market => (
                  <option key={market.name} value={market.name}>
                    {market.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Revenue Uplift</h3>
              
              <div className="input-group">
                <div className="flex justify-between mb-2">
                  <label htmlFor="dynamicPricing">Dynamic Pricing</label>
                  <span className="font-bold">{dpLabels[dynamicPricingLevel]}</span>
                </div>
                <input 
                  type="range"
                  id="dynamicPricing"
                  min="0"
                  max="3"
                  step="1"
                  value={dynamicPricingLevel}
                  onChange={(e) => setDynamicPricingLevel(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div className="mb-3">
                <div className="flex items-center gap-3">
                  <input 
                    type="checkbox" 
                    id="marketing"
                    checked={marketingEnabled} 
                    onChange={(e) => setMarketingEnabled(e.target.checked)}
                  />
                  <label htmlFor="marketing" className="flex items-center gap-2">
                    Marketing
                  </label>
                </div>
                {marketingEnabled && (
                  <div className="sub-options-container">
                    <div className="multi-select-container">
                      {marketingChannels.map(channel => (
                        <div key={channel} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`marketing-${channel}`}
                            value={channel}
                            checked={selectedMarketingChannels.includes(channel)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMarketingChannels([...selectedMarketingChannels, channel]);
                              } else {
                                setSelectedMarketingChannels(selectedMarketingChannels.filter(c => c !== channel));
                              }
                            }}
                          />
                          <label htmlFor={`marketing-${channel}`} className="ml-2 text-sm">
                            {channel}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-3 mt-4">
                <div className="flex items-center gap-3">
                  <input 
                    type="checkbox" 
                    id="enforcement"
                    checked={enforcementEnabled} 
                    onChange={(e) => setEnforcementEnabled(e.target.checked)}
                  />
                  <label htmlFor="enforcement" className="flex items-center gap-2">
                    Enforcement
                  </label>
                </div>
                {enforcementEnabled && (
                  <div className="sub-options-container">
                    {enforcementOptions.map(option => (
                      <div key={option} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`enforcement-${option}`}
                          value={option}
                          checked={selectedEnforcementOptions.includes(option)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEnforcementOptions([...selectedEnforcementOptions, option]);
                            } else {
                              setSelectedEnforcementOptions(selectedEnforcementOptions.filter(o => o !== option));
                            }
                          }}
                        />
                        <label htmlFor={`enforcement-${option}`} className="ml-2 text-sm">
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Capture Section */}
        <div className="grid main-grid mb-8">
          <div className="card">
            <h2 className="text-xl font-semibold mb-6 text-center">Gateless Revenue Capture Rate</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={captureChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis 
                  domain={[0, 'dataMax + 10']}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip formatter={(value) => [`${value.toFixed(1)}%`, 'Capture']} />
                <Legend />
                <Bar dataKey="Parking Revenue" stackId="a" fill={greenShades[2]} maxBarSize={100} />
                <Bar dataKey="Ticket Revenue" stackId="a" fill={greenShades[3]} radius={[4, 4, 0, 0]} maxBarSize={100} />
                <ReferenceLine y={100} stroke="black" strokeDasharray="4 4" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Capture Rate Control Panel */}
          <div className="card control-panel">
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-xl font-semibold">Revenue Capture</h2>
              </div>
              <div className="input-group">
                <div className="flex justify-between mb-2">
                  <label htmlFor="compliance">Compliance Rate</label>
                  <span className="font-bold">{complianceRate}%</span>
                </div>
                <input 
                  type="range"
                  id="compliance"
                  min="80"
                  max="100"
                  step="1"
                  value={complianceRate}
                  onChange={(e) => setComplianceRate(Number(e.target.value))}
                  className="w-full"
                />
              </div>
              <div className="input-group">
                <div className="flex justify-between mb-2">
                  <label htmlFor="ticketPrice">Ticket Price</label>
                  <span className="font-bold">${ticketPrice}</span>
                </div>
                <input 
                  type="range"
                  id="ticketPrice"
                  min="10"
                  max="100"
                  step="5"
                  value={ticketPrice}
                  onChange={(e) => setTicketPrice(Number(e.target.value))}
                  className="w-full"
                />
              </div>
              <div className="input-group">
                <div className="flex justify-between mb-2">
                  <label htmlFor="ticketPaymentRate">Ticket Payment Rate</label>
                  <span className="font-bold">{ticketPaymentRate}%</span>
                </div>
                <input 
                  type="range"
                  id="ticketPaymentRate"
                  min="10"
                  max="100"
                  step="5"
                  value={ticketPaymentRate}
                  onChange={(e) => setTicketPaymentRate(Number(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
        </div>

        {/* Driver Reviews Section */}
        <div className="mb-8">
          <GoogleReviews address={address} />
        </div>
      </main>
    </div>
  );
}

export default App; 